import CloseIcon from "@mui/icons-material/Close";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import LayersIcon from "@mui/icons-material/Layers";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { LayerGroup } from "../../../../../types";
import { Shape } from "../../RampEditor/RampCanvas/RampCanvas";
import { usePanelState } from "../stores/usePanelState";
import { LayerGroupRow } from "./LayerGroupRow";
import { LayerRow } from "./LayerRow";
import { PropertiesSection } from "./PropertiesSection";
import { LayerOperations } from "./useLayerOperations";
import { buildRenderItems, filterShapesBySearch } from "./utils";

export type LayerPanelPresenterProps = {
  // Data (read-only)
  shapes: Shape[];
  groups: LayerGroup[];
  activeShapes: string[];
  highlightedShapes: string[];

  // Operations (from hook)
  operations: LayerOperations;

  // Direct shapes setter for PropertiesSection
  setShapes: (shapes: Shape[]) => void;

  // Selection management
  setActiveShapes: (shapeIds: string[]) => void;

  // UI callbacks
  onClose: () => void;
  onCenterShape?: (shapeId: string) => void;
  canvasWidth?: number;
};

/**
 * Presenter component for LayerPanel.
 *
 * Responsibilities:
 * - UI rendering
 * - Local UI state (search, collapsed groups, focus, tabs)
 * - Keyboard navigation
 * - Hotkeys
 *
 * Does NOT contain any domain logic or state mutations.
 * All mutations go through operations prop.
 */
export const LayerPanelPresenter: React.FC<LayerPanelPresenterProps> = ({
  shapes,
  groups,
  activeShapes,
  highlightedShapes,
  operations,
  setShapes,
  setActiveShapes,
  onClose,
  onCenterShape,
  canvasWidth = 600,
}) => {
  const theme = useTheme();

  // ============= UI-only State =============
  const [searchQuery, setSearchQuery] = React.useState("");
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(
    new Set()
  );
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);
  const [selectionAnchor, setSelectionAnchor] = React.useState<string | null>(
    null
  );
  const [activeTab, setActiveTab] = React.useState<"layers" | "properties">(
    "layers"
  );

  // Global store for properties tab request
  const { propertiesTabRequested, clearPropertiesTabRequest } = usePanelState();
  React.useEffect(() => {
    if (propertiesTabRequested && activeShapes.length > 0) {
      setActiveTab("properties");
      clearPropertiesTabRequest();
    }
  }, [propertiesTabRequested, activeShapes.length, clearPropertiesTabRequest]);

  // ============= Derived Data =============
  const filteredShapes = React.useMemo(
    () => filterShapesBySearch(shapes, searchQuery),
    [shapes, searchQuery]
  );

  // Reset focus when search changes
  React.useEffect(() => {
    setFocusedIndex(-1);
  }, [searchQuery]);

  const visibleCount = shapes.filter((s) => s.visible !== false).length;
  const totalCount = shapes.length;

  // ============= UI Event Handlers =============

  const toggleGroupCollapsed = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleSelect = (
    shapeId: string,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => {
    const isShiftClick = modifiers.shiftKey;
    const isCmdClick = modifiers.metaKey || modifiers.ctrlKey;

    if (isShiftClick) {
      // Determine the anchor: use existing, or fallback to first active shape
      const effectiveAnchor =
        selectionAnchor || (activeShapes.length > 0 ? activeShapes[0] : null);

      if (effectiveAnchor) {
        const anchorIndex = filteredShapes.findIndex(
          (s) => s.id === effectiveAnchor
        );
        const targetIndex = filteredShapes.findIndex((s) => s.id === shapeId);

        if (anchorIndex !== -1 && targetIndex !== -1) {
          const start = Math.min(anchorIndex, targetIndex);
          const end = Math.max(anchorIndex, targetIndex);
          const rangeIds = filteredShapes
            .slice(start, end + 1)
            .map((s) => s.id);

          if (isCmdClick) {
            setActiveShapes([...new Set([...activeShapes, ...rangeIds])]);
          } else {
            setActiveShapes(rangeIds);
          }
          // Set anchor if it wasn't set before
          if (!selectionAnchor) {
            setSelectionAnchor(effectiveAnchor);
          }
          return;
        }
      }
      // Fallback: if no valid anchor or indices, treat as single selection
      setActiveShapes([shapeId]);
      setSelectionAnchor(shapeId);
    } else if (isCmdClick) {
      if (activeShapes.includes(shapeId)) {
        setActiveShapes(activeShapes.filter((id) => id !== shapeId));
      } else {
        setActiveShapes([...activeShapes, shapeId]);
        setSelectionAnchor(shapeId);
      }
    } else {
      setActiveShapes([shapeId]);
      setSelectionAnchor(shapeId);
      onCenterShape?.(shapeId);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    const maxIndex = filteredShapes.length - 1;
    if (maxIndex < 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
    } else if (e.key === "Enter" && focusedIndex >= 0) {
      const shape = filteredShapes[focusedIndex];
      if (shape) {
        handleSelect(shape.id, {
          shiftKey: e.shiftKey,
          metaKey: e.metaKey,
          ctrlKey: e.ctrlKey,
        });
      }
    } else if (e.key === "Escape") {
      setFocusedIndex(-1);
      setSelectionAnchor(null);
      setActiveShapes([]);
    }
  };

  // ============= Operations Wrappers =============
  // These wrap the operations to handle multi-selection and clear state as needed

  const handleDelete = (shapeId: string) => {
    const shapesToDelete =
      activeShapes.length > 0 && activeShapes.includes(shapeId)
        ? activeShapes
        : [shapeId];

    operations.deleteShapes(shapesToDelete);
    setActiveShapes([]);
    setSelectionAnchor(null);
  };

  const handleToggleLock = (shapeId: string) => {
    const shapesToToggle =
      activeShapes.length > 0 && activeShapes.includes(shapeId)
        ? activeShapes
        : [shapeId];

    operations.toggleLockMultiple(shapesToToggle);
  };

  const handleDeleteGroup = (groupId: string) => {
    operations.deleteGroup(groupId);
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
  };

  const handleGroupSelected = () => {
    if (activeShapes.length >= 2) {
      operations.createGroup(activeShapes);
    }
  };

  const handleUngroupSelected = () => {
    operations.ungroupShapes(activeShapes);
  };

  // ============= Hotkeys =============

  useHotkeys(
    ["meta+g", "ctrl+g"],
    (e) => {
      e.preventDefault();
      handleGroupSelected();
    },
    [activeShapes, operations]
  );

  useHotkeys(
    ["meta+shift+g", "ctrl+shift+g"],
    (e) => {
      e.preventDefault();
      handleUngroupSelected();
    },
    [activeShapes, operations]
  );

  // ============= Render Items =============

  const renderItems = React.useMemo(() => {
    const items: React.ReactNode[] = [];
    const renderItemsList = buildRenderItems(filteredShapes, groups);
    const groupsMap = new Map(groups.map((g) => [g.id, g]));

    // Helper to render a shape row
    const renderShapeRow = (shape: Shape, indented: boolean) => {
      const shapeId = shape.id;
      return (
        <Box key={shapeId} sx={{ pl: indented ? 2 : 0 }}>
          <LayerRow
            shape={shape}
            shapes={shapes}
            isSelected={activeShapes.includes(shapeId)}
            isHighlighted={highlightedShapes.includes(shapeId)}
            isFocused={
              focusedIndex >= 0 &&
              filteredShapes[focusedIndex]?.id === shapeId
            }
            onSelect={handleSelect}
            onToggleVisibility={() => operations.toggleVisibility(shapeId)}
            onToggleLock={() => handleToggleLock(shapeId)}
            onDelete={() => handleDelete(shapeId)}
            onRename={(newName) => operations.renameShape(shapeId, newName)}
            onMoveUp={() => operations.moveShape(shapeId, "up")}
            onMoveDown={() => operations.moveShape(shapeId, "down")}
            onAddToGroup={(targetGroupId) => operations.addToGroup(shapeId, targetGroupId)}
            onRemoveFromGroup={() => operations.removeFromGroup(shapeId)}
            canMoveUp={operations.canMoveShapeUp(shapeId)}
            canMoveDown={operations.canMoveShapeDown(shapeId)}
            availableGroups={groups}
          />
        </Box>
      );
    };

    // Render items in order (interleaved ungrouped shapes and groups)
    for (const item of renderItemsList) {
      if (item.type === "shape") {
        items.push(renderShapeRow(item.shape, false));
      } else {
        // Group
        const group = groupsMap.get(item.groupId);
        if (!group) continue;

        const groupShapeCount = shapes.filter(
          (s) => s.group_id === item.groupId
        ).length;

        // Render group header
        items.push(
          <LayerGroupRow
            key={`group-${item.groupId}`}
            group={group}
            isCollapsed={collapsedGroups.has(item.groupId)}
            childCount={groupShapeCount}
            isLocked={operations.isGroupLocked(item.groupId)}
            canMoveUp={operations.canMoveGroupUp(item.groupId)}
            canMoveDown={operations.canMoveGroupDown(item.groupId)}
            onToggleCollapse={() => toggleGroupCollapsed(item.groupId)}
            onRename={(name) => operations.renameGroup(item.groupId, name)}
            onDelete={() => handleDeleteGroup(item.groupId)}
            onToggleLock={() => operations.toggleGroupLock(item.groupId)}
            onMoveUp={() => operations.moveGroup(item.groupId, "up")}
            onMoveDown={() => operations.moveGroup(item.groupId, "down")}
          />
        );

        // Render shapes in this group (if expanded)
        if (!collapsedGroups.has(item.groupId)) {
          for (const shape of item.shapes) {
            items.push(renderShapeRow(shape, true));
          }
        }
      }
    }

    return items;
  }, [
    filteredShapes,
    shapes,
    groups,
    collapsedGroups,
    activeShapes,
    highlightedShapes,
    focusedIndex,
    operations,
  ]);

  // ============= Render =============

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* Header with tabs */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 0.5 }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              minWidth: "auto",
              py: 0.5,
              px: 1.5,
              fontSize: "0.8rem",
              textTransform: "none",
            },
          }}
        >
          <Tab
            value="layers"
            icon={<LayersIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>Layers</span>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="span"
                >
                  ({visibleCount}/{totalCount})
                </Typography>
              </Stack>
            }
          />
          <Tab
            value="properties"
            icon={<TuneIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Properties"
            disabled={activeShapes.length === 0}
          />
        </Tabs>
        <Stack direction="row" spacing={0.5}>
          {activeTab === "layers" && (
            <Tooltip
              title={
                activeShapes.length < 2
                  ? "Select 2+ shapes to group"
                  : "Create group from selection (⌘G)"
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleGroupSelected}
                  disabled={activeShapes.length < 2}
                >
                  <CreateNewFolderIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Close Panel">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Divider />

      {/* Layers tab content */}
      {activeTab === "layers" && (
        <>
          <Box sx={{ px: 1, py: 1 }}>
            <TextField
              placeholder="Search layers..."
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Divider />

          <Box
            sx={{
              flex: 1,
              minHeight: 100,
              overflowY: "auto",
              py: 0.5,
            }}
          >
            {filteredShapes.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ py: 2 }}
              >
                {searchQuery ? "No matching layers" : "No layers"}
              </Typography>
            ) : (
              renderItems
            )}
          </Box>
        </>
      )}

      {/* Properties tab content */}
      {activeTab === "properties" && activeShapes.length > 0 && (
        <PropertiesSection
          shapes={shapes}
          setShapes={setShapes}
          activeShapes={activeShapes}
          canvasWidth={canvasWidth}
        />
      )}

      {/* Footer */}
      <Divider />
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {activeTab === "layers"
            ? "Double-click to rename. Use arrows to reorder. ⌘G to group."
            : "Select shapes to edit properties."}
        </Typography>
      </Box>
    </Box>
  );
};
